<template lang="pug">
#store.equipment-tech-map.materials-tab
  .spinner-container.form-spinner(v-if="loading")
    q-spinner(color="primary", size="3em")

  .tech-map-card(v-if="!loading")
    .main-block
      .actions-block
        .button-wrapper(v-for="button in buttons", :key="button.name")
          q-btn.show-card-action(v-if="showButtonEdit(button)", flat, no-caps, @click="changeEditable(button)")
            template(v-if="ifButtonIconExists(button)", slot="default")
              inline-svg(:class="`${button.name}-icon`", :src="getButtonIcon(button)")
            template(v-if="ifButtonLabelExists(button)", slot="default")
              span.action-label {{ button.label }}
          q-btn.materials-import-button.import(
            v-else-if="button.name === 'import'",
            @click="$refs['grid-modals'].openImportForm({ data: [], href: '' })",
            flat,
            no-caps
          )
            template(slot="default")
              inline-svg.grid-btn-icon-left(:src="fileIcon")
            template(slot="default", v-if="button.label")
              span {{ button.label }}
          q-btn.materials-import-button.import(
            v-else-if="button.name === 'export'",
            @click="openExportForm()",
            flat,
            no-caps
          )
            template(slot="default")
              inline-svg.grid-btn-icon-left(:src="fileIcon")
            template(slot="default")
              span {{ exportExcelText }}

    .techmap-info-blocks
      .spinner-container.form-spinner(v-if="loadingBlocks")
        q-spinner(color="primary", size="3em")

      .specification-block(v-if="!loadingBlocks")
        specification(
          :editable="editable",
          :data="tableData",
          :tableColumns="tableColumns",
          :hasNextPage="hasNextPage",
          :loadingTable="loadingTable",
          :loadingMore="loadingMore",
          @reloadWithSort="reloadWithSort",
          @changeTableData="changeTableData",
          @loadMore="loadMore"
        )

    .button-group.row.justify-center(v-if="editable")
      q-btn.tech-map-form-submit(flat, no-caps, :label="submitButtonLabel", @click="onSubmit")
  grid-modals(
    ref="grid-modals",
    @reload-data="reloadWithSort",
    :parentData="{ grid: gridName, path: `/api/v3/stores/${parentData.rowId}/materials_stores` }"
  )
  q-dialog(v-model="showConfirmModal")
    confirm-modal(@confirm="onConfirmExport", :parentData="{ message: notifies.confirm_excel_export }")
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import specification from "../../specification/specification";
import ImportExcelForm from "@/components/shared/forms/importExcelForm";
import gridModals from "@/components/shared/grid/gridModals";
import confirmModal from "@/components/shared/helpers/confirmModal";
import backend from "@/api";
import { Notify } from "quasar";
import { notifies } from "@/services/useLocales";
import { handleError } from "@/services/handleErrors";
import i18n from "@/plugins/vue-i18n/index";
import { currentLocale } from "@/services/useLocales";
import { currentTimezoneOffset } from "@/services/useTimezoneHelpers";
import { useEmitter } from "@/services/useEmitter";

const emitter = useEmitter();

const props = defineProps({
  /**
   * Параметры от родителя. Пример: {
   *  grid: "stores",
   *  rowId: 1,
   *  row: {
        "id": 62,
        "title": "сумма",
        "facility_title": "Test facility",
        "store_head_name": "Doris Fahey Lind",
        "external_id": "",
        "is_archive": false,
        "address": "",
        "materials_list": {
            "import_button": {
                "template_path": "/api/v3/stores/62/materials_stores/excel_template",
                "button_label": "Загрузка из Эксель",
                "import_path": "/api/v3/stores/62/materials_stores/import_excel"
            }
        },
        "created_at": "2024-12-04T13:05:10.150Z",
        "updated_at": "2024-12-04T13:05:10.150Z"
      },
      tabData: {
        actions: [
          {
            "name": "edit",
            "label": "Редактировать",
            "icon": "edit.svg",
            "style": {
              "wrapper": {
                "width": "145px",
                "margin-right": "5px"
              },
              "button": {}
            },
            "onclick": {
                "child": "back",
                "fn_name": "setEditable",
                "attr": {
                  "value": true
              }
            }
          },
          {
            "name": "back",
            "label": "Назад",
            "icon": "back.svg",
            "style": {
              "wrapper": {
                "width": "95px",
                "margin-right": "5px",
                "display": "none"
              },
              "button": {}
            },
            "onclick": {
              "parent": "edit",
              "fn_name": "setEditable",
              "attr": {
                  "value": false
              }
            }
          }
        ],
        edit_materials_list_buttons: {
          "rows_handle": [
            {
              "name": "add",
              "label": "Добавить",
              "icon": "add.svg",
              "onclick": "addItem",
              "style": {
                "width": "150px",
                "margin-right": "2px"
              }
            },
            {
              "name": "duplicate",
              "label": "Дублировать",
              "icon": "duplicate.svg",
              "onclick": "duplicateItem",
              "checkable": true,
              "style": {
                "width": "175px",
                "margin-right": "2px"
              }
            },
            {
              "name": "delete",
              "label": "Удалить",
              "icon": "trash.svg",
              "onclick": "deleteItem",
              "checkable": true,
              "style": {
                "width": "150px",
                "margin-right": "2px"
              }
            }
          ],
          "submit": {
              "label": "Сохранить"
          }
        }
      }
   * }
   */
  parentData: { type: Object, default: () => {} },
});

const gridName = props.parentData.grid;
const { rowId } = props.parentData;
const { row } = props.parentData;

const tableData = ref([]);
const buttons = ref([]);
const submitButtonLabel = ref({});
const tableColumns = ref([]);
const editable = ref(false);
const loading = ref(true);
const loadingBlocks = ref(true);
const page = ref(1);
const currentSort = ref("id");
const descending = ref(true);
const query = ref("");
const hasNextPage = ref(false);
const showConfirmModal = ref(false);
const loadingMore = ref(false);
const loadingTable = ref(false);

const tabData = computed(() => props.parentData.tabData);
const fileIcon = computed(() => require(`../../../../../assets/icons/header/file.svg`));
const exportExcelText = computed(() => i18n.messages[currentLocale.value]["exportExcel"]);

const openExportForm = () => {
  showConfirmModal.value = true;
};

const onConfirmExport = async confirm => {
  if (!confirm) {
    showConfirmModal.value = false;
    return;
  }

  if (localStorage.getItem("report_id_waiting")) {
    Notify.create(notifies.value.wait_report_generated);
  } else {
    await sendReport();
  }
};

const sendReport = async () => {
  try {
    const reportParams = {
      filters: {
        store_id: rowId,
      },
      report_class: "store",
      report_type: "export_materials",
    };
    const params = {
      report_params: JSON.stringify(reportParams),
      time_zone: currentTimezoneOffset(),
    };

    const { data } = await backend.create("/api/v3/reports", null, { report: params });

    emitter.emit("init-report-intervals", data);
    Notify.create(notifies.value.report_in_process);
  } catch (error) {
    await handleError(error);
  } finally {
    showConfirmModal.value = false;
  }
};

const loadMore = async () => {
  page.value++;
  loadingMore.value = true;
  await loadData();
};

const changeTableData = (val, index, key) => {
  tableData.value[index][key] = val;
};

const showButtonEdit = button => {
  if (button.name === "edit" || button.name === "back") {
    if (editable.value === true) {
      return button.name !== "edit";
    } else {
      return button.name === "edit";
    }
  } else {
    return false;
  }
};

const getButtonIcon = button => {
  return require(`../../../../../assets/icons/ppr/equipment/show/actions/${button.icon}`);
};

const ifButtonIconExists = button => {
  return button.icon && button.icon.length > 0;
};

const ifButtonLabelExists = button => {
  return button.label && button.label.length > 0;
};

const changeEditable = button => {
  if (button.name === "edit") {
    editable.value = true;
  } else {
    editable.value = false;
  }
};

const setEditable = async attr => {
  if (!loadingBlocks.value) {
    editable.value = attr.value;

    if (!attr.value) {
      loadingBlocks.value = true;
      page.value = 1;
      tableData.value = [];

      await loadData();
    }
  }
};

const reloadWithSort = async params => {
  page.value = 1;
  tableData.value = [];

  if (params?.sort) {
    currentSort.value = params.sort;
    descending.value = params.sortDirection === "down";
  }

  if (params?.query) {
    query.value = params.query;
  } else {
    query.value = "";
  }

  loadingTable.value = true;
  await loadData(params);
};

const loadData = async params => {
  try {
    const json = {
      table: {
        rowsPerPage: 20,
        page: page.value,
        sortBy: currentSort.value,
        descending: descending.value,
      },
    };

    if (query.value) {
      json.table["query"] = query.value;
    }

    const { data } = await backend.index(`/api/v3/stores/${rowId}/materials_stores?json=${JSON.stringify(json)}`);

    tableData.value = tableData.value.concat(data.data);
    buttons.value = data.actions?.crud;
    submitButtonLabel.value = data.actions?.handle?.[0].name;
    if (page.value === 1) {
      tableColumns.value = data.columns;
    }

    hasNextPage.value = data.count - data.pagination.rowsPerPage * page.value > 0;
  } catch (error) {
    await handleError(error);
  } finally {
    loading.value = false;
    loadingBlocks.value = false;
    loadingMore.value = false;
    loadingTable.value = false;
  }
};

const onSubmit = async (params = {}) => {
  const req = tableData.value.map(item => ({
    id: item.id,
    cost: item.cost,
    count: item.count,
    store_sender_title: item.store_sender_title,
    store_motion_title: item.store_motion_title,
  }));

  params["stores"] = {
    materials_store: JSON.stringify(req),
  };

  try {
    await backend.update(`/api/v3/stores/${rowId}/materials_stores/update_records`, "", params);
    Notify.create(notifies.value.success_updated_entry);
  } catch (error) {
    await handleError(error);
  } finally {
    editable.value = false;
    tableData.value = [];
    await loadData();
    page.value = 1;
  }
};

onMounted(async () => {
  await loadData();
});
</script>

<style lang="scss">
#store .techmap-info-blocks,
#store .equipment-tech-map,
#store .tabs-block {
  min-height: 0;
}

#store {
  .specification-title span {
    font-size: 20px !important;
    color: var(--tab-text-color) !important;
  }
  &.equipment-tech-map .tech-map-card .specification-block {
    margin-top: 58px;
  }
  &.equipment-tech-map {
    position: relative;
    .tech-map-card {
      .tabs-block {
        max-height: calc(66vh - 100px);
        margin-top: 0;
        td {
          padding: 10px;
        }
        thead {
          position: absolute;
          display: table;
          table-layout: fixed;
          top: -40px;
        }
        tr {
          display: table;
          table-layout: fixed;
          width: 100%;
        }
      }
    }

    .show-card-action {
      border-radius: 8px;
      height: 40px;
      align-items: center;
      color: #5b7aff;
      font-weight: 600;
      border: 2px solid #5b7aff;
      width: fit-content;
      padding: 0 5px;

      .edit-icon,
      .back-icon {
        margin-right: 8px;
        path {
          fill: #5b7aff;
        }
      }
    }

    .materials-import-button {
      padding-left: 15px;
      padding-right: 15px;
      margin-left: 10px;
      border-radius: 8px;
      background-color: #5b7aff !important;
      color: #fff;
      height: 40px;
    }
  }
}

#components-table.stores td {
  color: var(--tab-text-color);
}

.equipment-tech-map.materials-tab {
  .tech-map-card .actions-block {
    display: flex;
    padding-top: 0 !important;
    padding-left: 0 !important;
    width: 100% !important;
  }
}
</style>
